import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, downloadPdf, formatApiError, inr, waLink } from "@/lib/api";
import { COST_LABELS } from "@/lib/constants";
import { PageHeader, Panel, marginCls } from "@/components/admin/common";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { quoteWhatsappText } from "./Quotes";

const INIT = { customer_name: "", phone: "", email: "", package_id: "none", travel_date: "", adults: 2, children: 0,
  markup_percent: 20, discount: 0, gst_percent: 0, valid_days: 7, notes: "", enquiry_id: null };
const n = (v) => Number(v) || 0;

const Summary = ({ c }) => (
  <Panel className="p-6 space-y-3" >
    <p className="font-semibold text-navy">Cost calculator</p>
    {[["Total cost (your cost)", c.cost, ""], ["Package price (after markup)", c.packagePrice, ""], ["Discount", -c.discount, "text-coral"], ["Selling price (before GST)", c.selling, "text-ocean"], ["GST", c.gst, ""]].map(([l, v, cls]) => (
      <div key={l} className="flex justify-between text-sm"><span className="text-slate-600">{l}</span><span className={`font-mono ${cls}`}>{inr(v)}</span></div>
    ))}
    <div className="border-t border-slate-200 pt-3 flex justify-between"><span className="font-semibold text-navy">Customer pays</span><span className="font-mono text-xl font-semibold text-navy" data-testid="quote-grand-total">{inr(c.grand)}</span></div>
    <div className="flex justify-between text-sm"><span className="text-slate-600">Per person</span><span className="font-mono">{inr(c.perPerson)}</span></div>
    <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 grid grid-cols-2 gap-2">
      <div><p className="text-xs text-slate-500">Your profit</p><p className={`font-mono text-lg font-semibold ${marginCls(c.margin)}`} data-testid="quote-profit">{inr(c.profit)}</p></div>
      <div><p className="text-xs text-slate-500">Profit margin</p><p className={`font-mono text-lg font-semibold ${marginCls(c.margin)}`} data-testid="quote-margin">{c.margin.toFixed(1)}%</p></div>
    </div>
  </Panel>
);

export default function QuoteBuilder() {
  const [params] = useSearchParams();
  const [pkgs, setPkgs] = useState([]);
  const [f, setF] = useState(INIT);
  const [items, setItems] = useState([{ label: "Tour cost", amount: 0 }]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    api.get("/admin/packages").then((r) => setPkgs(r.data));
    const eid = params.get("enquiry");
    if (eid) api.get(`/admin/enquiries/${eid}`).then(({ data: e }) => setF((s) => ({ ...s, customer_name: e.name, phone: e.phone, email: e.email || "",
      travel_date: e.travel_date, adults: e.adults || e.guests, children: e.children || 0, package_id: e.package_id || "none", enquiry_id: e.id }))).catch(() => toast.error("Enquiry not found"));
  }, [params]);

  const pax = Math.max(n(f.adults) + n(f.children), 1);
  useEffect(() => {
    const p = pkgs.find((x) => x.id === f.package_id);
    if (!p) return;
    setItems(Object.entries(COST_LABELS).map(([k, label]) => ({ label, amount: Math.round((p.cost[k] || 0) * pax) })));
    if (p.cost_price) setF((s) => ({ ...s, markup_percent: Math.round((p.selling_price / p.cost_price - 1) * 1000) / 10 }));
  }, [f.package_id, pax, pkgs]);

  const c = useMemo(() => {
    const cost = items.reduce((a, i) => a + n(i.amount), 0);
    const packagePrice = cost * (1 + n(f.markup_percent) / 100);
    const selling = Math.max(packagePrice - n(f.discount), 0);
    const profit = selling - cost;
    const gst = (selling * n(f.gst_percent)) / 100;
    return { cost, packagePrice, discount: n(f.discount), selling, profit, gst, grand: selling + gst, perPerson: (selling + gst) / pax, margin: selling ? (profit / selling) * 100 : 0 };
  }, [items, f, pax]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const field = (k, label, props = {}) => (<div className="space-y-1.5"><Label>{label}</Label><Input value={f[k]} onChange={set(k)} data-testid={`quote-${k}-input`} {...props} /></div>);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/quotes", { ...f, adults: n(f.adults), children: n(f.children), markup_percent: n(f.markup_percent), discount: n(f.discount),
        gst_percent: n(f.gst_percent), valid_days: n(f.valid_days) || 7, package_id: f.package_id === "none" ? null : f.package_id,
        items: items.filter((i) => i.label.trim()).map((i) => ({ label: i.label, amount: n(i.amount) })) });
      setSaved(data);
      toast.success(`Quotation ${data.quote_no} saved`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  if (saved) return (
    <Panel className="p-10 max-w-xl mx-auto text-center" data-testid="quote-saved-panel">
      <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
      <h2 className="mt-4 text-2xl font-bold text-navy">Quotation {saved.quote_no} ready</h2>
      <p className="mt-1 text-slate-500">{saved.customer_name} · {inr(saved.grand_total)} · margin {saved.margin}%</p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
        <Button className="bg-ocean hover:bg-navy" onClick={() => downloadPdf(`/admin/quotes/${saved.id}/pdf`, `${saved.quote_no}.pdf`)} data-testid="admin-download-pdf-button"><Download className="h-4 w-4 mr-2" />Download PDF</Button>
        {saved.phone && <a href={waLink(saved.phone, quoteWhatsappText(saved))} target="_blank" rel="noreferrer" data-testid="quote-saved-whatsapp-button" className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"><WhatsAppIcon className="h-4 w-4" />Send on WhatsApp</a>}
      </div>
      <div className="mt-6 flex gap-4 justify-center text-sm">
        <button onClick={() => { setSaved(null); setF(INIT); setItems([{ label: "Tour cost", amount: 0 }]); }} className="text-ocean font-semibold" data-testid="quote-new-another-button">New quotation</button>
        <Link to="/admin/quotes" className="text-ocean font-semibold" data-testid="quote-view-all-link">All quotations</Link>
      </div>
    </Panel>
  );

  return (
    <div data-testid="admin-quote-builder">
      <PageHeader title="New quotation" subtitle="Pick a package to auto-fill costs, adjust line items and markup, then save to generate a PDF." />
      <form onSubmit={save} className="grid gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2 space-y-6">
          <Panel className="p-6 grid gap-4 sm:grid-cols-2">
            <p className="sm:col-span-2 font-semibold text-navy">Customer & trip</p>
            {field("customer_name", "Customer name *", { required: true, minLength: 2 })}
            {field("phone", "Phone / WhatsApp")}
            {field("email", "Email", { type: "email" })}
            {field("travel_date", "Travel date", { type: "date" })}
            <div className="space-y-1.5 sm:col-span-2"><Label>Package</Label>
              <Select value={f.package_id} onValueChange={(v) => setF((s) => ({ ...s, package_id: v }))}>
                <SelectTrigger data-testid="quote-package-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-72"><SelectItem value="none">Custom tour (no package)</SelectItem>{pkgs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {field("adults", "Adults *", { type: "number", min: 1, required: true })}
            {field("children", "Children", { type: "number", min: 0 })}
          </Panel>
          <Panel className="p-6 space-y-3">
            <div className="flex items-center justify-between"><p className="font-semibold text-navy">Cost line items (total for group)</p>
              <Button type="button" size="sm" variant="outline" onClick={() => setItems((l) => [...l, { label: "", amount: 0 }])} data-testid="quote-add-item-button"><Plus className="h-4 w-4 mr-1" />Add item</Button></div>
            <p className="text-xs text-slate-500">Auto-filled as package cost per person × {pax} traveller(s). Adjust for child rates, extra nights or activities.</p>
            {items.map((it, i) => (
              <div key={i} className="flex gap-2">
                <Input value={it.label} onChange={(e) => setItems((l) => l.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="Description" data-testid={`quote-item-label-${i}`} />
                <Input type="number" min={0} step="any" className="w-36 font-mono" value={it.amount} onChange={(e) => setItems((l) => l.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} data-testid={`quote-item-amount-${i}`} />
                <Button type="button" size="icon" variant="ghost" className="text-rose-600 shrink-0" disabled={items.length === 1} onClick={() => setItems((l) => l.filter((_, j) => j !== i))} data-testid={`quote-item-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </Panel>
          <Panel className="p-6 grid gap-4 sm:grid-cols-4">
            {field("markup_percent", "Markup %", { type: "number", min: 0, step: "any" })}
            {field("discount", "Discount (₹)", { type: "number", min: 0, step: "any" })}
            {field("gst_percent", "GST %", { type: "number", min: 0, max: 28, step: "any" })}
            {field("valid_days", "Valid for (days)", { type: "number", min: 1, max: 90 })}
            <div className="space-y-1.5 sm:col-span-4"><Label>Notes for customer</Label><Textarea rows={3} value={f.notes} onChange={set("notes")} data-testid="quote-notes-input" /></div>
          </Panel>
        </div>
        <div className="space-y-4 xl:sticky xl:top-24 self-start">
          <Summary c={c} />
          <Button type="submit" disabled={saving} className="w-full h-12 bg-ocean hover:bg-navy text-base" data-testid="admin-calculate-quote-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save quotation & create PDF"}
          </Button>
        </div>
      </form>
    </div>
  );
}
